import { Button, Group, Select, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { IconDownload, IconSearch } from "@tabler/icons-react";
import { serverLogExportUrl } from "./server-log.api";
import { EMPTY_FILTERS, type ServerLogFilters } from "./server-log.types";
import { useServerLogText } from "./use-server-log-text";

const LEVEL_VALUES = ["warn", "error", "fatal"] as const;
const STATUS_VALUES = ["open", "resolved", "ignored", "regressed"] as const;
const SEARCH_WIDTH = 260;
const SELECT_WIDTH = 170;
const RANGE_WIDTH = 240;

interface Props {
	value: ServerLogFilters;
	modules: string[];
	onChange: (next: ServerLogFilters) => void;
}

/** Baris filter: pencarian, level, modul, status, rentang waktu, ekspor CSV. */
export function ServerLogFiltersBar({ value, modules, onChange }: Props) {
	const t = useServerLogText();
	const set = (patch: Partial<ServerLogFilters>) =>
		onChange({ ...value, ...patch });

	return (
		<Group align="flex-end" wrap="wrap" gap="sm">
			<TextInput
				label={t.filters.search}
				leftSection={<IconSearch size={14} />}
				value={value.q}
				onChange={(e) => set({ q: e.currentTarget.value })}
				w={SEARCH_WIDTH}
			/>
			<Select
				label={t.filters.level}
				placeholder={t.filters.all}
				data={LEVEL_VALUES.map((v) => ({ value: v, label: t.levels[v] ?? v }))}
				value={value.level}
				onChange={(level) => set({ level })}
				clearable
				w={SELECT_WIDTH}
			/>
			<Select
				label={t.filters.module}
				placeholder={t.filters.all}
				data={modules}
				value={value.module}
				onChange={(module) => set({ module })}
				clearable
				searchable
				w={SELECT_WIDTH}
			/>
			<Select
				label={t.filters.status}
				placeholder={t.filters.all}
				data={STATUS_VALUES.map((v) => ({
					value: v,
					label: t.statuses[v] ?? v,
				}))}
				value={value.status}
				onChange={(status) => set({ status })}
				clearable
				w={SELECT_WIDTH}
			/>
			<DatePickerInput
				type="range"
				label={t.filters.range}
				value={value.range}
				onChange={(range) =>
					set({
						range: [
							range[0] ? new Date(range[0]) : null,
							range[1] ? new Date(range[1]) : null,
						],
					})
				}
				clearable
				w={RANGE_WIDTH}
			/>
			<Button variant="subtle" onClick={() => onChange(EMPTY_FILTERS)}>
				{t.filters.reset}
			</Button>
			<Button
				variant="light"
				leftSection={<IconDownload size={14} />}
				component="a"
				href={serverLogExportUrl(value)}
			>
				{t.filters.export}
			</Button>
		</Group>
	);
}
